import { Component, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { AccountFormData, FormDataService } from '../../core/services/form-data-service';
import { domainNameValidatorRegex, domainValidatorApps, loadDomainExtraAttributes, strongPasswordValidator, usernameValidator, passwordsMatchValidator, office365DomainValidator, office365BaseNameValidator, emailFormatValidator, noWhitespaceValidator, phoneNumberValidator } from '../../core/validators/validator';
import { SignupAPIService } from '../../core/services/signup-apiservice';
import { HttpClient } from '@angular/common/http';
import { SummaryService } from '../../core/services/summary-service';
import { NavigationService } from '../../core/services/navigation-service';
import { Flow } from '../../core/utils/flow-utils';
import { TextService } from '../../core/services/text-service';
import { environment } from '../../../environments/environment';
import { RecaptchaComponent } from 'ng-recaptcha';
import { firstValueFrom, timeout, TimeoutError } from 'rxjs';
import { CloudOfficeResponse } from '../../core/models/shared.models';
import { waitForFormValidation } from '../../core/utils/form-validation-utils';

/**
 * Shared Account Form Component
 * Handles user account creation with domain registration options
 * Supports both domain purchase and existing domain scenarios
 */
@Component({
  selector: 'app-shared-acccount-form',
  templateUrl: './shared-acccount-form.html',
  styleUrls: ['./shared-acccount-form.scss'],
  standalone: false
})
export class SharedAcccountForm {
  isValidating = false;
  environment = environment;
  accountForm!: FormGroup;

  // Password visibility toggles
  showPassword = false;
  showConfirm = false;

  // US domain registration extra attributes
  usNexusOptions: any[] = [];    // US nexus relationship options
  usCountryOptions: any[] = [];  // US country options
  usPurposeOptions: any[] = [];  // US purpose options

  flow: Flow;

  /**
   * Constructor - Inject required services
   * @param fb - FormBuilder for reactive forms
   * @param formData - Service for form data persistence
   * @param signupApi - API service for signup operations
   * @param summaryService - Service for managing summary data
   * @param http - HTTP client for API calls
   */
  constructor(private fb: FormBuilder,
    private formData: FormDataService,
    private signupApi: SignupAPIService,
    private summaryService: SummaryService,
    private navService: NavigationService,
    public textService: TextService,
    private http: HttpClient) {
    this.flow = this.navService.getFlow();
    const root = document.querySelector('app-top-menu');

    if (this.flow == 'reseller') {
      root?.classList.remove('menu-open');
    } else {
      root?.classList.add('menu-open');
    }
  }


  /**
   * Component initialization
   * Sets up the reactive form with validators and subscriptions
   */
  ngOnInit(): void {
    const domainValidators =
      this.flow === 'reseller'
        ? []
        : [Validators.required, domainNameValidatorRegex(), noWhitespaceValidator()];
  
    this.accountForm = this.fb.group(
      {
        business: [''],
        domain: this.fb.control('', {
          validators: domainValidators,
          updateOn: 'change'
        }),
        domainOption: ['own'],
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        username: this.fb.control('', {
          validators: [
            Validators.required,
            Validators.minLength(4),
            Validators.maxLength(15)
          ],
          updateOn: 'blur'
        }),
        password: ['', [Validators.required, strongPasswordValidator]],
        confirmPassword: ['', Validators.required],
        securityQuestion: ['teacher', Validators.required],
        securityAnswer: ['', Validators.required],
        email: ['', [Validators.required, emailFormatValidator()]],
        phone: ['', [Validators.required, phoneNumberValidator(this.formData)]],
        usNexus: [''],
        usCountry: [''],
        usPurpose: ['']
      },
      { validators: [passwordsMatchValidator] }
    );
  
    // OFFICE365 FLOW ------------------------------------------------------
    if (this.flow === 'office365') {
      const domainCtrl = this.accountForm.get('domain')!;
      const domainOptionCtrl = this.accountForm.get('domainOption')!;
  
      domainCtrl.clearValidators();
      domainCtrl.clearAsyncValidators();
  
      domainCtrl.setValidators([
        Validators.required,
        noWhitespaceValidator(),
        office365BaseNameValidator() // plain name only
      ]);
  
      domainCtrl.setAsyncValidators([
        office365DomainValidator(this.signupApi)
      ]);
  
      domainCtrl.updateValueAndValidity({ emitEvent: false });
      domainOptionCtrl.disable({ emitEvent: false });
    }
  
    // APPS (NOT reseller, NOT office365) ---------------------------------
    if (this.flow !== 'reseller' && this.flow !== 'office365') {
      // reactive validation update on domainOption change
      this.accountForm.get('domainOption')?.valueChanges.subscribe(opt => {
        const domainCtrl = this.accountForm.get('domain')!;
  
        domainCtrl.clearValidators();
        domainCtrl.clearAsyncValidators();
  
        if (opt === 'own') {
          domainCtrl.setValidators([
            Validators.required,
            domainNameValidatorRegex(),
            noWhitespaceValidator()
          ]);
        }
  
        if (opt === 'buy') {
          domainCtrl.setValidators([
            Validators.required,
            domainNameValidatorRegex(),
            noWhitespaceValidator()
          ]);
          domainCtrl.setAsyncValidators([
            domainValidatorApps(this.signupApi)
          ]);
        }
  
        domainCtrl.updateValueAndValidity({ emitEvent: true });
        // updateDomainValidators() will be called by valueChanges subscription below
      });
  
      // re-run validators on domain text change
      this.accountForm.get('domain')?.valueChanges.subscribe(() => {
        this.updateDomainValidators();
      });
      // this.accountForm.get('domain')?.statusChanges.subscribe(status => {
      //   console.log('STATUS:', status);
      // });

      // this.accountForm.get('domain')?.valueChanges.subscribe(value => {
      //   console.log('VALUE:', value);
      // });
      // summary update
      this.accountForm.get('domain')?.statusChanges.subscribe(status => {
        const option = this.accountForm.get('domainOption')?.value;
        const value = this.accountForm.get('domain')?.value?.trim();
  
        if (option === 'own') {
          this.summaryService.setDomain(null);
          return;
        }
  
        if (status === 'VALID' && value) {
         // console.log(status);
          const base = { domainName: value, years: 1, dnsType: 'pointtous' };
          if (option === 'buy') {
            this.summaryService.setDomain({ ...base, tldPrice: 10 });
          } else {
            this.summaryService.setDomain({ ...base, tldPrice: 0 });
          }
        } else {
          this.summaryService.setDomain(null);
        }
      });
    } else {
      this.summaryService.setDomain(null);
    }
  
    // loading US TLD extra attributes ------------------------------------
    loadDomainExtraAttributes(this.http).subscribe(data => {
      this.usNexusOptions =
        data.attribute.find((a: any) => a.name === 'us_nexus')?.options.option || [];
      this.usCountryOptions =
        data.attribute.find((a: any) => a.name === 'global_cc_us')?.options.option || [];
      this.usPurposeOptions =
        data.attribute.find((a: any) => a.name === 'us_purpose')?.options.option || [];
    });
  
    // restore saved form values ------------------------------------------
    const saved = this.formData.getAccountData();
    if (saved) {
      this.accountForm.patchValue(saved);

      const phoneCtrl = this.accountForm.get('phone');
      if (phoneCtrl?.value) {
        phoneCtrl.updateValueAndValidity({ emitEvent: true });
      }
  
      const usernameCtrl = this.accountForm.get('username');
      if (usernameCtrl) {
        usernameCtrl.markAsTouched({ onlySelf: true });
        usernameCtrl.updateValueAndValidity({
          onlySelf: true,
          emitEvent: true
        });
      }
  
      const domainCtrl = this.accountForm.get('domain');
      if (domainCtrl) {
        const opt = this.accountForm.get('domainOption')?.value;
  
        if (this.flow !== 'office365') {
          domainCtrl.clearValidators();
          domainCtrl.clearAsyncValidators();
  
          if (opt === 'buy') {
            domainCtrl.setValidators([
              Validators.required,
              domainNameValidatorRegex(),
              noWhitespaceValidator()
            ]);
            domainCtrl.setAsyncValidators([domainValidatorApps(this.signupApi)]);
          } else {
            domainCtrl.setValidators([
              Validators.required,
              domainNameValidatorRegex(),
              noWhitespaceValidator()
            ]);
          }
        }
  
        domainCtrl.setValue(saved.domain ?? '');
        domainCtrl.updateValueAndValidity({ emitEvent: true });
  
        const base = { domainName: saved.domain, years: 1, dnsType: 'pointtous' };
        if (this.flow !== 'office365') {
          if (opt === 'buy') {
            this.summaryService.setDomain({ ...base, tldPrice: 10 });
          } else {
            this.summaryService.setDomain({ ...base, tldPrice: 0 });
          }
        }
      }
    }
  }
  

  /**
   * Validates the entire form including:
   * - Angular field-level validators
   * - Final Cloud Office validation (domain, username, password)
   *
   * Marks all fields as touched to show UI errors.
   * Returns the form data only if ALL validation passes.
   *
   * @returns AccountFormData or null if any validation fails
   */

  async getValue(token: string): Promise<AccountFormData | null> {
    if (this.isValidating) {
      return null;
    }

    this.isValidating = true;

    try {
      this.accountForm.updateValueAndValidity();
      this.accountForm.markAllAsTouched();

      await waitForFormValidation(this.accountForm);

      if (!this.accountForm.valid) {
        return null;
      }

      const value = this.accountForm.value as AccountFormData;
      const usernameCtrl = this.accountForm.get('username');
      const domainCtrl = this.accountForm.get('domain');
      const passwordCtrl = this.accountForm.get('password');

      const payload =
        this.flow === 'apps'
          ? {
              username: value.username,
              domainName: value.domain,
              password: value.password,
            }
          : {
              username: value.username,
              password: value.password,
            };

      usernameCtrl?.markAsPending();

      let res: CloudOfficeResponse | null = null;
      try {
        res = await firstValueFrom(
          this.signupApi.cloudOfficeValidation(payload, token).pipe(timeout(25_000))
        );
      } catch (err: unknown) {
        const httpErr = err as { error?: { message?: string }; message?: string };
        this.navService.errorMessage =
          err instanceof TimeoutError
            ? this.textService.getText('error_message')
            : httpErr?.error?.message ||
              httpErr?.message ||
              this.textService.getText('error_message');
        usernameCtrl?.updateValueAndValidity({ emitEvent: false });
        return null;
      }

      usernameCtrl?.updateValueAndValidity({ emitEvent: false });

      if (!res) {
        return null;
      }

      if (this.isCloudOfficeTrue(res.domainTaken)) {
        domainCtrl?.setErrors({ domainTaken: true });
        domainCtrl?.markAsTouched({ onlySelf: true });
        return null;
      }

      if (this.isCloudOfficeFalse(res.adminAvail)) {
        usernameCtrl?.setErrors({ cloudOffice: true });
        usernameCtrl?.markAsTouched({ onlySelf: true });
        return null;
      }

      if (this.isCloudOfficeFalse(res.passwordValid)) {
        passwordCtrl?.setErrors({ cloudOffice: true });
        passwordCtrl?.markAsTouched({ onlySelf: true });
        return null;
      }

      return value;
    } finally {
      this.isValidating = false;
    }
  }

  private isCloudOfficeTrue(value: string | boolean | undefined): boolean {
    return value === true || String(value).toLowerCase() === 'true';
  }

  private isCloudOfficeFalse(value: string | boolean | undefined): boolean {
    return value === false || String(value).toLowerCase() === 'false';
  }
  

  /**
   * Mark all form fields as touched to trigger validation display
   * Used when form submission is attempted with invalid data
   */
  markAllTouched() {
    this.accountForm.markAllAsTouched();
    this.accountForm.updateValueAndValidity();
  }

  /**
   * Update domain validators based on domain option and TLD
   * - Adds async domain availability validator only when buying domains
   * - Requires US extra fields only for .us domains when buying
   * - Clears US field values when not applicable
   */
  private updateDomainValidators() {
    if (this.flow === 'reseller') return;
  
    const c = this.accountForm.get('domain')!;
    const option = this.accountForm.get('domainOption')?.value;
    const raw = (c.value ?? '').trim().toLowerCase();
  
    if (this.flow === 'office365') {
      c.clearValidators();
      c.clearAsyncValidators();
      c.setValidators([Validators.required, noWhitespaceValidator(), office365BaseNameValidator()]);
      c.setAsyncValidators([office365DomainValidator(this.signupApi)]);
      c.updateValueAndValidity({ emitEvent: false });
      return;
    }
  
     c.clearValidators();
     c.clearAsyncValidators();
  
    if (option === 'own') {
      c.setValidators([Validators.required, domainNameValidatorRegex(), noWhitespaceValidator()]);
    }
  
    if (option === 'buy') {
      c.setValidators([Validators.required, domainNameValidatorRegex(), noWhitespaceValidator()]);
      c.setAsyncValidators([domainValidatorApps(this.signupApi)]);
    }
  
    const isUS = raw.endsWith('.us');
  
    const nexus = this.accountForm.get('usNexus');
    const country = this.accountForm.get('usCountry');
    const purpose = this.accountForm.get('usPurpose');
  
    if (option === 'buy' && isUS) {
      nexus?.setValidators([Validators.required]);
      country?.setValidators([Validators.required]);
      purpose?.setValidators([Validators.required]);
    } else {
      nexus?.clearValidators();
      country?.clearValidators();
      purpose?.clearValidators();
      nexus?.setValue('');
      country?.setValue('');
      purpose?.setValue('');
    }
  
    // Don't call updateValueAndValidity here to avoid infinite loop
    // The validation will be triggered by the valueChanges subscription
    // c.updateValueAndValidity({ emitEvent: false });
    nexus?.updateValueAndValidity({ emitEvent: false });
    country?.updateValueAndValidity({ emitEvent: false });
    purpose?.updateValueAndValidity({ emitEvent: false });
  }

  fillMockData() {
    this.accountForm.patchValue({
      business: 'Racker. Inc',
      domain: 'racker-demo-stg.com',
      domainOption: 'buy',
      firstName: 'Racker',
      lastName: 'Rackington',
      username: 'rackertothecore',
      password: 'StrongPassw0rd!',
      confirmPassword: 'StrongPassw0rd!',
      securityQuestion: 'What was your childhood nickname?',
      securityAnswer: 'Mr. Racker',
      email: 'racker.rackington@rackspace.com',
      phone: '+1 201 559 4773',
      usNexus: 'C11',
      usCountry: 'US',
      usPurpose: 'P3'
    });

    this.accountForm.get('phone')?.updateValueAndValidity({ emitEvent: true });
    this.accountForm.markAllAsTouched();
  }


}
